import { useTracking } from '@act/react-components/tracking';
import { Button } from '@act/storybook';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { ClockCounterClockwiseIcon, PlusIcon } from '@phosphor-icons/react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NeedDetailHistory from '../NeedDetailHistory';
import { NeedsDetailAddAction } from '../NeedsDetailAddAction';
import {
  ConsolidateDuplicateNeedsDialog,
  TransferNeedDialog,
} from '../../need-administration';
import { NeedDetailState } from '../hooks/useNeedDetailState.hook';
import { AddNeedContent } from '../AddNeedContent';

interface NeedDetailDialogsProps {
  state: NeedDetailState;
}

export const NeedDetailDialogs = ({ state }: NeedDetailDialogsProps) => {
  const { need, refreshNeeds, isEditable } = state;

  if (!need) {
    return null;
  }

  return (
    <>
      {state.transferDialogOpen && (
        <TransferNeedDialog
          open={state.transferDialogOpen}
          onClose={() => state.setTransferDialogOpen(false)}
          need={need}
          onSuccess={refreshNeeds}
        />
      )}
      {state.consolidateDialogOpen && (
        <ConsolidateDuplicateNeedsDialog
          open={state.consolidateDialogOpen}
          onClose={() => state.setConsolidateDialogOpen(false)}
          memberId={need.memberId}
          onSuccess={refreshNeeds}
        />
      )}
      {state.editNeed && isEditable && (
        <AddNeedContent
          open={!!state.editNeed}
          onClose={() => state.setEditNeed(null)}
          need={need}
        />
      )}
      {!state.editNeed && (state.isAddActionOpen || state.editAction) && isEditable && (
        <NeedsDetailAddAction
          open={state.isAddActionOpen || !!state.editAction}
          onClose={() => {
            state.setIsAddActionOpen(false);
            state.setEditAction(null);
          }}
          need={need}
          action={state.editAction}
        />
      )}
    </>
  );
};

interface NeedDetailAdminMenuProps {
  state: NeedDetailState;
}

export const NeedDetailAdminMenu = ({ state }: NeedDetailAdminMenuProps) => {
  if (!state.hasAdminPermission) {
    return null;
  }

  return (
    <Box sx={{ ml: 'auto' }}>
      <IconButton
        onClick={e => state.setAdminMenuAnchor(e.currentTarget)}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={state.adminMenuAnchor}
        open={Boolean(state.adminMenuAnchor)}
        onClose={() => state.setAdminMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            state.setAdminMenuAnchor(null);
            state.setTransferDialogOpen(true);
          }}
        >
          Transfer Need
        </MenuItem>
        <MenuItem
          onClick={() => {
            state.setAdminMenuAnchor(null);
            state.setConsolidateDialogOpen(true);
          }}
        >
          Consolidate Duplicate Needs
        </MenuItem>
      </Menu>
    </Box>
  );
};

interface NeedDetailHistorySectionProps {
  state: NeedDetailState;
}

export const NeedDetailHistorySection = ({
  state,
}: NeedDetailHistorySectionProps) => {
  const { generateTrackingProps } = useTracking();

  if (!state.need) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ClockCounterClockwiseIcon size={24} />
          <Typography variant="titleMedium">History</Typography>
        </Box>
        {state.isEditable && (
          <Box sx={{ display: 'flex' }}>
            <Button
              variant="text"
              size="large"
              startIcon={<PlusIcon />}
              onClick={() => state.setIsAddActionOpen(true)}
              {...generateTrackingProps('open-add-action-from-call-button')}
            >
              <Typography variant="titleMedium" fontWeight="semibold">
                Add Action
              </Typography>
            </Button>
          </Box>
        )}
      </Box>
      <NeedDetailHistory
        need={state.need}
        editMode={state.isEditable}
        detailMode={true}
        setEdit={state.setEditAction}
        setEditNeed={state.setEditNeed}
      />
    </>
  );
};
